import React from 'react';
import { string, func, bool } from 'prop-types';
import FroalaEditor from 'react-froala-wysiwyg';
import NativeFroalaEditor from 'froala-editor';
import Label from './label';
import { classNames } from 'utils';
import { stripTags, removeStyles } from 'utils/string';
import './input-editor.scss';

import 'froala-editor/js/froala_editor.pkgd.min.js';
import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';

// Froala plugins for toolbar buttons
import 'froala-editor/js/plugins/align.min.js';
import 'froala-editor/js/plugins/colors.min.js';
import 'froala-editor/css/plugins/colors.min.css';
import 'froala-editor/js/plugins/code_beautifier.min.js';
import 'froala-editor/js/plugins/code_view.min.js';
import 'froala-editor/css/plugins/code_view.min.css';
import 'froala-editor/js/plugins/draggable.min.js';
import 'froala-editor/css/plugins/draggable.min.css';
import 'froala-editor/js/plugins/emoticons.min.js';
import 'froala-editor/css/plugins/emoticons.min.css';
import 'froala-editor/js/plugins/entities.min.js';
import 'froala-editor/js/plugins/image.min.js';
import 'froala-editor/css/plugins/image.min.css';
import 'froala-editor/js/plugins/line_breaker.min.js';
import 'froala-editor/css/plugins/line_breaker.min.css';
import 'froala-editor/js/plugins/link.min.js';
import 'froala-editor/js/plugins/lists.min.js';
import 'froala-editor/js/plugins/paragraph_format.min.js';
import 'froala-editor/js/plugins/quote.min.js';
import 'froala-editor/js/plugins/table.min.js';
import 'froala-editor/css/plugins/table.min.css';
import 'froala-editor/js/plugins/url.min.js';
import 'froala-editor/js/plugins/video.min.js';
import 'froala-editor/css/plugins/video.min.css';
import 'froala-editor/js/plugins/word_paste.min.js';

// Full screen plugin is causing layout issues
/* import 'froala-editor/js/plugins/fullscreen.min.js';
import 'froala-editor/css/plugins/fullscreen.min.css'; */

// Repositions a Froala popup to the left if it overflows the editor column's right edge
function repositionPopupIfOverflow(editor, popupId) {
  requestAnimationFrame(() => {
    const popup = editor.popups.get(popupId);
    if (!popup) return;
    const el = popup.get(0);
    if (!el) return;
    const toolbar = el.closest('.fr-toolbar');
    if (!toolbar) return;
    const toolbarRect = toolbar.getBoundingClientRect();
    const rect = el.getBoundingClientRect();
    // Check against the toolbar's right edge (popup should stay within the toolbar)
    const overflow = rect.right - (toolbarRect.right + 8);
    if (overflow > 0) {
      const newLeft = toolbarRect.width - rect.width;
      el.style.left = Math.max(0, newLeft) + 'px';
    }
  });
}

export default class InputEditor extends React.Component {
  static contextTypes = {
    openModal: func,
  };

  static propTypes = {
    toolbar: string,
    toolbarOptions: string,
    placeholder: string,
    multiline: bool,
    onChange: func,
    id: string,
    label: string,
    help: string,
    pastePlain: bool,
    baseTagsOnly: bool,
    disabled: bool,
  };

  constructor(props) {
    super(props);

    // special toolbar
    const toolbarOptions = props.toolbarOptions
      ? props.toolbarOptions.split(',')
      : null;

    // allow only listed tags
    const additionalOptions = {};
    if (props.baseTagsOnly) {
      additionalOptions.htmlAllowedTags = [
        'a',
        'br',
        'em',
        'link',
        'strong',
        'u',
      ];
    }
    this.state = {
      model: this.getInitialModel(props),
      value: '',
      config: {
        ...this.froalaConfig,
        ...additionalOptions,
        placeholderText: props.placeholder || this.froalaConfig.placeholderText,
        // Multi-line by default unless rich_text toolbar is used
        multiLine:
          props.toolbar === 'rich_text'
            ? false
            : props.multiline !== undefined
            ? Boolean(props.multiline)
            : true,
        toolbarButtons: props.disabled
          ? []
          : toolbarOptions ||
            this.toolbars[props.toolbar] ||
            this.toolbars.rich,
        editable: !props.disabled,
        pastePlain: props.pastePlain,
        events: {
          'paste.afterCleanup': (pastedText) => {
            return props.pastePlain
              ? stripTags(pastedText)
              : removeStyles(pastedText);
          },
          'popups.show.link.insert': function () {
            repositionPopupIfOverflow(this, 'link.insert');
          },
          'popups.show.video.insert': function () {
            repositionPopupIfOverflow(this, 'video.insert');
          },
          'popups.show.table.insert': function () {
            repositionPopupIfOverflow(this, 'table.insert');
          },
          'popups.show.image.insert': function () {
            repositionPopupIfOverflow(this, 'image.insert');
          },
        },
      },
    };

    this.changeTimeout = 0;
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.value !== this.state.value) {
      this.setState({
        model: nextProps.value,
        value: nextProps.value,
      });
    }
  }

  componentWillUnmount() {
    if (this.changeTimeout) {
      clearTimeout(this.changeTimeout);
    }
  }

  // checks if placeholder text changed
  shouldComponentUpdate(nextProps) {
    const oldPlaceholder = this.props.placeholder;
    const { placeholder } = nextProps;

    const oldValue = this.props.value;
    const { value } = nextProps;

    return oldPlaceholder !== placeholder || oldValue !== value;
  }

  // update placeholder
  componentDidUpdate(prevProps) {
    if (prevProps.placeholder !== this.props.placeholder) {
      const { editor } = this.refs;
      if (!editor) return;

      const { element } = editor;
      if (!element) return;

      const froalaEl = element['data-froala.editor'];

      if (!froalaEl) return;

      if (froalaEl.opts) {
        froalaEl.opts.placeholderText = this.props.placeholder;
      }
      if (froalaEl.placeholder) {
        froalaEl.placeholder.refresh();
      }
    }
  }

  getInitialModel(props) {
    return (props.value || props.defaultValue || '').toString();
  }

  onModelChange = (model) => {
    // Debounce because there is a lot happening here
    this.setState({ model });

    if (this.changeTimeout) {
      clearTimeout(this.changeTimeout);
    }

    this.changeTimeout = setTimeout(() => {
      this.changeTimeout = 0;
      this.setState({ value: model });

      const event = new Event('change', { bubbles: true });

      if (this.refs.input) {
        this.refs.input.value = model;
        this.refs.input.dispatchEvent(event);
        this.props.onChange?.(event);
      }
    }, 100);
  };

  onInsertImage = (url) => {
    this.refs.editor.editor.image.insert(url, true);
  };

  openImageModal = () => {
    this.context.openModal('InsertImage', {
      onInsert: this.onInsertImage,
    });
  };

  registerFroalaCommands = () => {
    const component = this;

    NativeFroalaEditor.DefineIcon('insertImage', {
      NAME: 'insertImage',
      SVG_KEY: 'insertImage',
    });

    NativeFroalaEditor.RegisterCommand('insertImage', {
      title: 'Insert Image',
      undo: false,
      focus: true,
      showOnMobile: true,
      refreshAfterCallback: true,
      callback: () => component.openImageModal(),
    });
  };

  // Large devices (≥ 1200px)
  toolbars = {
    rich: {
      moreText: {
        buttons: [
          'bold',
          'italic',
          'underline',
          'strikeThrough',
          'textColor',
          'backgroundColor',
          'clearFormatting',
          'paragraphFormat',
          'formatUL',
          'formatOL',
          'outdent',
          'indent',
          'align',
          'insertLink',
          'insertTable',
          'insertImage',
          'insertVideo',
          // 'fullscreen', Breaking layout
          'clearFormatting',
          'html',
        ],
        buttonsVisible: 20,
      },
    },
    rich_html: {
      moreText: {
        buttons: [
          'bold',
          'italic',
          'underline',
          'insertLink',
          'formatOL',
          'formatUL',
        ],
        buttonsVisible: 6,
      },
    },
    rich_text: {
      moreText: {
        buttons: ['bold', 'italic', 'insertLink'],
        buttonsVisible: 4,
      },
    },
  };

  froalaConfig = {
    // v3 & v4
    key: process.env.REACT_APP_FROALA_KEY,
    toolbarSticky: false,
    charCounterCount: false,
    placeholderText: 'Write something',
    pluginsEnabled: [
      'link',
      'lists',
      'paragraphFormat',
      'wordPaste',
      'bold',
      'italic',
      'underline',
      'strikeThrough',
      'colors',
      'clearFormatting',
      'align',
      'table',
      'image',
      'video',
      // 'fullscreen', Breaking layout
      'codeView',
      'codeBeautifier',
      'draggable',
      'emoticons',
      'entities',
      'lineBreaker',
      'quote',
      'url',
    ],
    imageEditButtons: [
      'imageAlign',
      'imageRemove',
      'imageLink',
      'linkOpen',
      'linkEdit',
      'linkRemove',
      '-',
      'imageDisplay',
      'imageAlt',
      'imageSize',
    ],
    videoInsertButtons: ['videoBack', '|', 'videoByURL', 'videoEmbed'],
    linkInsertButtons: ['linkBack'],
    linkEditButtons: ['linkOpen', 'linkEdit', 'linkRemove'],
    // Setting iframe: true causes TypeError: Cannot read property 'disableBlur' of undefined
    // setting it to false fixes the error, still an open issue
    // https://github.com/froala/react-froala-wysiwyg/issues/274 | https://github.com/froala/wysiwyg-editor/issues/4342
    iframe: false,
    heightMax: 500,
    heightMin: this.props.toolbar === 'rich_text' ? 38 : 160,
    heightAuto: true,
    quickInsertButtons: false,
    enter: NativeFroalaEditor.ENTER_BR,
    attribution: false,
  };

  render() {
    const { id, label, help, disabled } = this.props;

    return (
      <div className={classNames('form-editor', { disabled })}>
        <Label
          className="form-field-label"
          label={label}
          help={help}
          htmlFor={id}
        />

        <input type="hidden" ref="input" />

        <div
          className="form-field-input"
          onMouseEnter={disabled ? undefined : this.registerFroalaCommands}
        >
          <FroalaEditor
            id={id}
            config={this.state.config}
            model={this.state.model}
            onModelChange={this.onModelChange}
            ref="editor"
            tag="textarea"
          />
        </div>
      </div>
    );
  }
}
